import { Controller, type Control, type FieldErrors, type UseFormRegister } from 'react-hook-form'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'

export const SERVER_SIZES = [
  { value: 't3.small', label: 't3.small (2 vCPU, 2 GB RAM)' },
  { value: 't3.medium', label: 't3.medium (2 vCPU, 4 GB RAM)' },
  { value: 't3.large', label: 't3.large (2 vCPU, 8 GB RAM)' },
  { value: 't3.xlarge', label: 't3.xlarge (4 vCPU, 16 GB RAM)' },
] as const

export interface AsteriskConfigFormValues {
  asteriskInstanceSize: string
  pjsipConf: string
  requiresElasticIp: boolean
}

// Props accept specific types but can be passed wider form types via type assertion
interface AsteriskConfigFormProps {
  register: UseFormRegister<AsteriskConfigFormValues>
  control: Control<AsteriskConfigFormValues>
  errors: FieldErrors<AsteriskConfigFormValues>
  instanceSizeDisabled?: boolean // Only disable instance size, not other fields
  hideElasticIp?: boolean // Hide elastic IP checkbox after instance created
}

export function AsteriskConfigForm({
  register,
  control,
  errors,
  instanceSizeDisabled = false,
  hideElasticIp = false,
}: AsteriskConfigFormProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Asterisk Configuration</CardTitle>
        <CardDescription>Configure the Asterisk instance settings</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="asteriskInstanceSize">Server Size</Label>
          <Controller
            name="asteriskInstanceSize"
            control={control}
            render={({ field }) => (
              <Select
                value={field.value}
                onValueChange={field.onChange}
                disabled={instanceSizeDisabled}
              >
                <SelectTrigger id="asteriskInstanceSize">
                  <SelectValue placeholder="Select server size" />
                </SelectTrigger>
                <SelectContent>
                  {SERVER_SIZES.map((size) => (
                    <SelectItem key={size.value} value={size.value}>
                      {size.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
          {instanceSizeDisabled && (
            <p className="text-detail text-gray-500">
              Server size can only be changed when instance is down and no tasks are in progress
            </p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="pjsipConf">pjsip.conf</Label>
          <Textarea
            {...register('pjsipConf')}
            id="pjsipConf"
            rows={12}
            className="font-mono"
            placeholder="Enter pjsip.conf content..."
          />
          {errors.pjsipConf && (
            <p className="text-detail text-red-500">{errors.pjsipConf.message}</p>
          )}
        </div>

        {!hideElasticIp && (
          <div className="flex items-center space-x-2">
            <Controller
              name="requiresElasticIp"
              control={control}
              render={({ field }) => (
                <Checkbox
                  id="requiresElasticIp"
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              )}
            />
            <Label htmlFor="requiresElasticIp" className="font-medium">
              Allocate Elastic IP address
            </Label>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
